import React, { useEffect, useState } from "react";
import api, { money } from "@/lib/api";
import AdminLayout from "@/pages/admin/AdminLayout";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogTrigger } from "@/components/ui/dialog";
import { toast } from "sonner";

export default function AdminTradeIns() {
  const [rows, setRows] = useState([]);
  const [staff, setStaff] = useState([]);
  const [filter, setFilter] = useState("");

  const load = () => {
    const q = filter ? `?status=${encodeURIComponent(filter)}` : "";
    api.get(`/admin/tradeins${q}`).then(({ data }) => setRows(data));
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [filter]);
  useEffect(() => { api.get("/admin/staff").then(({ data }) => setStaff(data)); }, []);

  const act = async (fn) => { try { await fn(); load(); } catch (e) { toast.error(e.response?.data?.detail || "Failed"); } };

  const summary = {
    "New": rows.filter((r) => r.status === "Requested").length,
    "Scheduled": rows.filter((r) => r.status === "Scheduled").length,
    "Weighed": rows.filter((r) => r.status === "Weighed").length,
    "Credited": rows.filter((r) => r.status === "Points Credited").length,
  };

  return (
    <AdminLayout title="Trade-In Management">
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-4">
        {Object.entries(summary).map(([k, v]) => (
          <div key={k} className="bg-card border border-border rounded-lg p-4"><div className="text-xs text-muted-foreground">{k}</div><div className="font-heading font-extrabold text-2xl font-mono-points">{v}</div></div>
        ))}
      </div>

      <div className="flex gap-2 mb-3 flex-wrap">
        {["", "Requested", "Scheduled", "Pickup Assigned", "Weighed", "Accepted", "Points Credited", "Rejected"].map((s) => (
          <button key={s} onClick={() => setFilter(s)} data-testid={`ti-filter-${s || "all"}`}
            className={`text-xs font-semibold px-3 py-1.5 rounded-md ${filter === s ? "bg-primary text-primary-foreground" : "bg-card border border-border"}`}>{s || "All"}</button>
        ))}
      </div>

      <div className="space-y-3">
        {rows.length === 0 && <div className="text-muted-foreground text-center py-10 bg-card border border-border rounded-lg">No trade-ins.</div>}
        {rows.map((t) => <TradeRow key={t.trade_id} t={t} staff={staff} act={act} />)}
      </div>
    </AdminLayout>
  );
}

function TradeRow({ t, staff, act }) {
  const [weight, setWeight] = useState(t.actual_weight || "");
  const [reason, setReason] = useState("");
  const [pd, setPd] = useState(t.pickup_date);
  const [ps, setPs] = useState(staff[0]?.user_id || "");

  return (
    <div className="bg-card border border-border rounded-lg p-4" data-testid={`admin-ti-${t.trade_id}`}>
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div>
          <div className="font-heading font-bold font-mono-points">#{t.trade_id} · {t.customer_name}</div>
          <div className="text-xs text-muted-foreground">{t.address} · pickup {t.pickup_date} ({t.pickup_time})</div>
          <div className="text-xs text-muted-foreground">Est ~{t.estimated_kg}kg → ~{money(t.estimated_points)} {t.actual_weight ? `· actual ${t.actual_weight}kg` : ""} {t.assigned_staff_name ? `· ${t.assigned_staff_name}` : ""}</div>
        </div>
        <span className="text-xs font-semibold px-2 py-1 rounded-md bg-primary/15 text-primary">{t.status}</span>
      </div>

      <div className="mt-3 flex flex-wrap gap-2 items-end">
        {t.status === "Requested" && (
          <Dialog>
            <DialogTrigger asChild><Button size="sm" data-testid={`schedule-${t.trade_id}`}>Schedule</Button></DialogTrigger>
            <DialogContent><DialogHeader><DialogTitle>Schedule pickup</DialogTitle></DialogHeader>
              <Label>Pickup date</Label><Input type="date" value={pd} onChange={(e) => setPd(e.target.value)} />
              <DialogFooter><Button onClick={() => act(() => api.post(`/admin/tradeins/${t.trade_id}/schedule`, { pickup_date: pd, pickup_time: t.pickup_time }))}>Confirm</Button></DialogFooter>
            </DialogContent>
          </Dialog>
        )}
        {(t.status === "Scheduled") && staff.length > 0 && (
          <div className="flex gap-2 items-end">
            <div><Label className="text-xs">Assign staff</Label>
              <select value={ps} onChange={(e) => setPs(e.target.value)} className="h-8 rounded-md border border-input bg-background px-2 text-sm" data-testid={`assign-select-${t.trade_id}`}>
                {staff.map((s) => <option key={s.user_id} value={s.user_id}>{s.name}</option>)}
              </select>
            </div>
            <Button size="sm" onClick={() => act(() => api.post(`/admin/tradeins/${t.trade_id}/assign`, { staff_id: ps }))} data-testid={`assign-${t.trade_id}`}>Assign</Button>
          </div>
        )}
        {["Pickup Assigned", "Scheduled", "Collected"].includes(t.status) && (
          <div className="flex gap-2 items-end">
            <div><Label className="text-xs">Actual weight (kg)</Label><Input type="number" step="0.1" value={weight} onChange={(e) => setWeight(e.target.value)} className="h-8 w-28" data-testid={`weight-${t.trade_id}`} /></div>
            <Button size="sm" variant="secondary" onClick={() => act(() => api.post(`/staff/pickups/${t.trade_id}/collect`, { actual_weight: parseFloat(weight) }))} data-testid={`record-weight-${t.trade_id}`}>Record weight</Button>
          </div>
        )}
        {t.status === "Weighed" && (
          <>
            <Button size="sm" onClick={() => act(() => api.post(`/admin/tradeins/${t.trade_id}/accept`))} data-testid={`accept-${t.trade_id}`}>Accept</Button>
          </>
        )}
        {t.status === "Accepted" && (
          <Button size="sm" onClick={() => act(() => api.post(`/admin/tradeins/${t.trade_id}/credit`))} data-testid={`credit-${t.trade_id}`}>Credit ₹{Math.round((t.actual_weight || 0) * 10)} Points</Button>
        )}
        {!["Points Credited", "Rejected", "Cancelled"].includes(t.status) && (
          <Dialog>
            <DialogTrigger asChild><Button size="sm" variant="outline" className="text-destructive" data-testid={`reject-btn-${t.trade_id}`}>Reject</Button></DialogTrigger>
            <DialogContent><DialogHeader><DialogTitle>Reject trade-in</DialogTitle></DialogHeader>
              <Label>Reason</Label><Input value={reason} onChange={(e) => setReason(e.target.value)} placeholder="e.g. rice not eligible" data-testid={`reject-reason-${t.trade_id}`} />
              <DialogFooter><Button variant="destructive" onClick={() => act(() => api.post(`/admin/tradeins/${t.trade_id}/reject`, { reason }))}>Confirm Reject</Button></DialogFooter>
            </DialogContent>
          </Dialog>
        )}
        {t.status === "Points Credited" && <span className="text-sm font-semibold text-[hsl(var(--secondary))]">✓ Credited {money(t.points_credited)}</span>}
      </div>
    </div>
  );
}
